const MAX_CAPTURES = 40;

const STATUS_RANK = {
    saved: 6,
    saving: 5,
    ready: 4,
    retrieving: 3,
    confirm: 2,
    error: 1,
    open: 0,
};

function storageKey(userId) {
    return `ipm_prospect_captures_${userId || 'anon'}`;
}

const STREET_TYPE = /\b(street|st|avenue|ave|road|rd|drive|dr|lane|ln|close|crescent|cres|boulevard|blvd|way|place|pl)\b/g;
const SKIP_TOKEN = /^(south|africa|rsa|ah|ext|extension)$/;

/** Fold Rd/Road, drop country + postcode so Google and the deeds list match. */
export function normalizeCapturePlace(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[.,/#]/g, ' ')
        .replace(/\b(south africa|rsa)\b/g, ' ')
        .replace(/\b\d{4}\b/g, ' ')
        .replace(STREET_TYPE, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function streetKey(value) {
    const n = normalizeCapturePlace(value);
    const num = (n.match(/^(\d+[a-z]?)\b/) || [])[1] || '';
    const after = num ? n.slice(n.indexOf(num) + num.length).trim() : n;
    const street = after.split(' ').find((w) => w.length >= 3 && !SKIP_TOKEN.test(w) && !/^\d+$/.test(w)) || '';
    return num && street ? `${num} ${street}` : '';
}

function streetNameFromKey(key) {
    return String(key || '').replace(/^\d+[a-z]?\s+/, '').trim();
}

/** Suburb / township tokens only — never the street name itself. */
function suburbTokens(value) {
    const street = streetNameFromKey(streetKey(value));
    return normalizeCapturePlace(value)
        .split(' ')
        .filter((w) => w.length >= 4 && !SKIP_TOKEN.test(w) && !/^\d+[a-z]?$/.test(w) && w !== street);
}

function placesMatch(x, y) {
    const na = normalizeCapturePlace(x);
    const nb = normalizeCapturePlace(y);
    if (!na || !nb) return false;
    if (na === nb) return true;
    const shorter = na.length <= nb.length ? na : nb;
    const longer = na.length <= nb.length ? nb : na;
    if (shorter.length >= 10 && longer.startsWith(`${shorter} `)) return true;
    const ka = streetKey(x);
    const kb = streetKey(y);
    if (!ka || ka !== kb) return false;
    const ta = suburbTokens(x);
    const tb = suburbTokens(y);
    // One side is only the street (Google pin vs deeds row). Both suburbs
    // present must overlap — 18 Topaas Douglasdale is not Eldorado Park.
    if (!ta.length || !tb.length) return true;
    return ta.some((t) => tb.includes(t));
}

function placeFields(row) {
    if (!row) return [];
    return [row.address, row.title, row.query].map((s) => String(s || '').trim()).filter(Boolean);
}

/**
 * Same in-progress capture — not a vault PDF guess.
 * Google "Pierre Rd" and the deeds list "Pierre Road, Ruimsig AH" are one house.
 * 18 Topaas Douglasdale is not 18 Topaas Eldorado Park.
 */
export function sameCapturePlace(a, b) {
    if (!a || !b) return false;
    if (a.id && b.id && a.id === b.id) return true;
    if (a.lsJobId && b.lsJobId && a.lsJobId === b.lsJobId) return true;
    const as = placeFields(a);
    const bs = placeFields(b);
    for (let i = 0; i < as.length; i += 1) {
        for (let j = 0; j < bs.length; j += 1) {
            if (placesMatch(as[i], bs[j])) return true;
        }
    }
    return false;
}

function richerText(a, b) {
    const sa = String(a || '').trim();
    const sb = String(b || '').trim();
    if (!sa) return sb;
    if (!sb) return sa;
    return sb.length > sa.length ? sb : sa;
}

export function preferCapture(a, b) {
    if (!a || !a.id) return b;
    if (!b || !b.id) return a;
    const ra = STATUS_RANK[a.status] || 0;
    const rb = STATUS_RANK[b.status] || 0;
    let pick;
    if ((a.status === 'retrieving' && b.status === 'confirm') || (b.status === 'retrieving' && a.status === 'confirm')) {
        pick = a.status === 'retrieving' ? a : b;
    } else {
        const newer = (b.updatedAt || 0) >= (a.updatedAt || 0) ? b : a;
        pick = rb > ra ? b : ra > rb ? a : newer;
    }
    return {
        ...a,
        ...pick,
        id: a.id,
        lsJobId: b.lsJobId || a.lsJobId || null,
        snapshot: pick.snapshot || a.snapshot || b.snapshot || null,
        savedFile: pick.savedFile || a.savedFile || b.savedFile || null,
        title: richerText(a.title, b.title),
        address: richerText(a.address, b.address),
        updatedAt: Math.max(a.updatedAt || 0, b.updatedAt || 0, pick.updatedAt || 0),
    };
}

export function mergeDuplicateCaptures(list) {
    const out = [];
    (list || []).forEach((row) => {
        if (!row || !row.id) return;
        const idx = out.findIndex((r) => sameCapturePlace(r, row));
        if (idx < 0) out.push(row);
        else out[idx] = preferCapture(out[idx], row);
    });
    return out;
}

export function slimCaptureSnapshot(snap) {
    if (!snap || typeof snap !== 'object') return null;
    const comp = snap.comp && typeof snap.comp === 'object' ? { ...snap.comp } : snap.comp || null;
    if (comp) {
        delete comp.agent_photo;
        delete comp.listingAgentPhoto;
    }
    return {
        comp,
        location: snap.location || null,
        form: snap.form || null,
        popiaAgentAcknowledged: Boolean(snap.popiaAgentAcknowledged),
        noteText: snap.noteText || '',
        crmStep: snap.crmStep === 'location' ? 'location' : 'details',
    };
}

export function readProspectCaptures(userId) {
    if (typeof localStorage === 'undefined') return [];
    try {
        const list = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
        const raw = Array.isArray(list) ? list.filter((row) => row && row.id) : [];
        const merged = mergeDuplicateCaptures(raw);
        if (merged.length !== raw.length) {
            try { localStorage.setItem(storageKey(userId), JSON.stringify(merged)); } catch (_) { /* quota */ }
        }
        return merged;
    } catch (_) {
        return [];
    }
}

export function upsertProspectCapture(userId, row) {
    if (!row || !row.id) return readProspectCaptures(userId);
    const nextRow = {
        ...row,
        userId: userId || row.userId || null,
        updatedAt: row.updatedAt || Date.now(),
        snapshot: slimCaptureSnapshot(row.snapshot) || row.snapshot || null,
    };
    const prev = readProspectCaptures(userId);
    const match = prev.find((r) => r.id === nextRow.id || sameCapturePlace(r, nextRow));
    const merged = match ? preferCapture(match, nextRow) : nextRow;
    const rest = prev.filter((r) => r.id !== merged.id && !sameCapturePlace(r, merged));
    const next = [merged, ...rest].slice(0, MAX_CAPTURES);
    try { localStorage.setItem(storageKey(userId), JSON.stringify(next)); } catch (_) { /* quota */ }
    return next;
}

export function writeProspectCaptures(userId, list) {
    const next = mergeDuplicateCaptures(Array.isArray(list) ? list.filter((row) => row && row.id) : []).slice(0, MAX_CAPTURES);
    try { localStorage.setItem(storageKey(userId), JSON.stringify(next)); } catch (_) { /* ignore */ }
    return next;
}

export function removeProspectCapture(userId, id) {
    const next = readProspectCaptures(userId).filter((r) => r.id !== id);
    try { localStorage.setItem(storageKey(userId), JSON.stringify(next)); } catch (_) { /* ignore */ }
    return next;
}

function dismissedKey(userId) {
    return `ipm_prospect_captures_dismissed_${userId || 'anon'}`;
}

export function readDismissedCaptureKeys(userId) {
    if (typeof localStorage === 'undefined') return [];
    try {
        const list = JSON.parse(localStorage.getItem(dismissedKey(userId)) || '[]');
        return Array.isArray(list) ? list.filter(Boolean) : [];
    } catch (_) {
        return [];
    }
}

export function rememberDismissedCaptureKeys(userId, keys) {
    const next = [...new Set([...readDismissedCaptureKeys(userId), ...(keys || []).filter(Boolean)])].slice(-80);
    try { localStorage.setItem(dismissedKey(userId), JSON.stringify(next)); } catch (_) { /* ignore */ }
    return next;
}

export function isCaptureDismissed(userId, row) {
    if (!row) return false;
    const keys = new Set(readDismissedCaptureKeys(userId));
    if (row.id && keys.has(row.id)) return true;
    if (row.lsJobId && keys.has(row.lsJobId)) return true;
    return false;
}

/** Hide the second address line when the title already is that street. */
export function captureAddressSubtitle(title, address) {
    const t = String(title || '').trim();
    const a = String(address || '').trim();
    if (!a || a === t) return '';
    if (sameCapturePlace({ address: t }, { address: a })) return '';
    const nt = normalizeCapturePlace(t);
    const na = normalizeCapturePlace(a);
    if (na && nt.includes(na)) return '';
    return a;
}

export function captureStatusLabel(status) {
    if (status === 'retrieving') return 'Retrieving details';
    if (status === 'confirm') return 'Pick the address';
    if (status === 'ready') return 'Report ready — finish capture';
    if (status === 'error') return 'Retrieval had an issue';
    if (status === 'saved') return 'Saved to CRM';
    if (status === 'saving') return 'Saving…';
    return 'In progress';
}
